import React, { useState, forwardRef, useImperativeHandle } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { TextField, Select, Grid, MenuItem, FormControl, InputLabel, FormHelperText } from '@mui/material'

import { scopeTypes, textFieldTypes, variableTypes, variantTypes } from '../../../../constants'
import { Logger, LoggerTypes } from '../../../../utils'
import { FormTitle } from '../../../forms'
import { iconNames } from '../../../icons'

const defaultValue = {
  id: null,
  name: null,
  description: null,
  initial_value: '0',
  type: 'NUMBER', // variableTypes.NUMBER
  unit: null
}

export const AttributeVariableForm = forwardRef(
  ({ setOpenDialogVariable, title, variable, dispatchAction, errors }, ref) => {
    const { t } = useTranslation()

    const initial_valueValue = `${variable?.initial_value ? variable.initial_value : defaultValue.initial_value}`

    const [id] = useState(variable?.id || defaultValue.id)
    const [name, setName] = useState(variable?.name || defaultValue.name)
    const [description, setDescription] = useState(variable?.description || defaultValue.description)
    const [initial_value, setInitialValue] = useState(initial_valueValue)
    const [type, setType] = useState(variable?.type || defaultValue.type)
    const [unit, setUnit] = useState(variable?.unit || defaultValue.unit)

    const isDisabled = variable?.scope === scopeTypes.IMPORT

    const onChangeName = (e) => {
      const _value = e.target.value
      Logger.trace(LoggerTypes.CONTROLLER, 'AttributeVariableForm.onChangeName()', { name: _value })
      setName(_value)
    }

    const onChangeDescription = (e) => {
      const _value = e.target.value
      Logger.trace(LoggerTypes.CONTROLLER, 'AttributeVariableForm.onChangeDescription()', { description: _value })
      setDescription(_value)
    }

    const onChangeInitialValue = (e) => {
      const _value = e.target.value
      Logger.trace(LoggerTypes.CONTROLLER, 'AttributeVariableForm.onChangeInitialValue()', { initial_value: _value })
      setInitialValue(_value)
    }

    const onChangeUnit = (e) => {
      const _value = e.target.value
      Logger.trace(LoggerTypes.CONTROLLER, 'AttributeVariableForm.onChangeUnit()', { unit: _value })
      setUnit(_value)
    }

    const onChangeType = (e) => {
      const _value = e.target.value
      Logger.trace(LoggerTypes.CONTROLLER, 'AttributeVariableForm.onChangeType()', { type: _value })
      setType(_value)
    }

    const onCloseDialog = () => {
      Logger.trace(LoggerTypes.CONTROLLER, 'AttributeVariableForm.onCloseDialog()')
      setOpenDialogVariable(false)
    }

    // ----

    useImperativeHandle(ref, () => ({
      onSubmitForm() {
        let newVariable = {
          name: name === '' ? null : name,
          description: description === '' ? null : description,
          initial_value: initial_value === '' ? null : initial_value,
          type: type === '' ? null : type
        }
        if (type !== variableTypes.PERCENTAGE) {
          newVariable.unit = unit === '' ? null : unit
        }

        Logger.trace(LoggerTypes.CONTROLLER, 'AttributeVariableForm.onSubmitForm()', {
          newVariable,
          variable
        })

        const _onSuccess = (res) => {
          Logger.trace(LoggerTypes.CONTROLLER, 'AttributeVariableForm.onSubmitForm()._onSuccess()', { res })

          // -- close popin only in case of success
          onCloseDialog()
        }

        dispatchAction(newVariable, id, _onSuccess)
      }
    }))

    return (
      <div className="AttributeForm AttributeVariableForm">
        <FormTitle title={title} icon={iconNames.VARIABLE} iconSize={22} />
        {isDisabled ? <div className="importGlobal">{t('global.title')}</div> : ''}
        <form>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={6}>
              <Grid container spacing={2}>
                {/*  name  */}
                <Grid item xs={12} sm={12}>
                  <TextField
                    type={textFieldTypes.TEXT}
                    name="name"
                    id="name"
                    value={name}
                    disabled={isDisabled}
                    variant={variantTypes.OUTLINED}
                    label={t('attributes.variables.name.label')}
                    className="common_fullwidth"
                    onChange={onChangeName}
                    placeholder={t('attributes.variables.name.placeholder')}
                    error={Boolean(errors && errors?.name)}
                    helperText={(errors && errors?.name) || ''}
                  />
                </Grid>

                {/*  type  */}
                <Grid item xs={12} sm={12} className="common_fullwidth">
                  <FormControl error={Boolean(errors && errors?.type)} className="common_fullwidth">
                    <InputLabel style={{ backgroundColor: 'white', padding: '0 5px' }}>
                      {t('attributes.variables.type.label')}
                    </InputLabel>
                    <Select
                      className="common_fullwidth"
                      value={type}
                      disabled={isDisabled}
                      defaultValue={variableTypes.PERCENTAGE}
                      onChange={onChangeType}
                      variant={variantTypes.OUTLINED}
                    >
                      <MenuItem value={variableTypes.NUMBER}>
                        {t(`attributes.variables.type.${String(variableTypes.NUMBER).toLowerCase()}`)}
                      </MenuItem>
                      <MenuItem value={variableTypes.PERCENTAGE}>
                        {t(`attributes.variables.type.${String(variableTypes.PERCENTAGE).toLowerCase()}`)}
                      </MenuItem>
                    </Select>
                    <FormHelperText>{(errors && errors?.type) || ''}</FormHelperText>
                  </FormControl>
                </Grid>

                {/*  value / unit  */}
                <Grid item xs={12} sm={12} className="common_fullwidth">
                  {/*  value  */}
                  <TextField
                    type={textFieldTypes.NUMBER}
                    name="initialValue"
                    id="initialValue"
                    value={initial_value}
                    label={t('attributes.variables.initial_value.label')}
                    onChange={onChangeInitialValue}
                    style={{ width: '254px' }}
                    placeholder={t('attributes.variables.initial_value.placeholder')}
                    variant={variantTypes.OUTLINED}
                    error={Boolean(errors && errors?.initial_value)}
                    helperText={(errors && errors?.initial_value) || ''}
                  />

                  {/*  unit  */}
                  <TextField
                    type={textFieldTypes.STRING}
                    name="unit"
                    id="unit"
                    disabled={type === variableTypes.PERCENTAGE || isDisabled}
                    value={type === variableTypes.PERCENTAGE ? '%' : unit}
                    InputLabelProps={{ shrink: true }}
                    label={t('attributes.variables.unit.label')}
                    onChange={onChangeUnit}
                    style={{ width: '150px', marginLeft: '14px' }}
                    placeholder={t('attributes.variables.unit.placeholder')}
                    variant={variantTypes.OUTLINED}
                    error={Boolean(errors && errors?.unit)}
                    helperText={(errors && errors?.unit) || ''}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={6} sm={6}>
              {/*  description  */}
              <Grid item xs={12} sm={12}>
                <TextField
                  type={textFieldTypes.TEXT}
                  name="description"
                  id="description"
                  multiline={true}
                  rows={7.2}
                  disabled={isDisabled}
                  label={t('attributes.variables.description.label')}
                  onChange={onChangeDescription}
                  value={description}
                  variant={variantTypes.OUTLINED}
                  className="common_fullwidth"
                  placeholder={t('attributes.variables.description.placeholder')}
                  error={Boolean(errors && errors?.description)}
                  helperText={(errors && errors?.description) || ''}
                />
              </Grid>
            </Grid>
          </Grid>
        </form>
      </div>
    )
  }
)

AttributeVariableForm.displayName = 'AttributeVariableForm'

AttributeVariableForm.propTypes = {
  setOpenDialogVariable: PropTypes.func.isRequired,
  dispatchAction: PropTypes.func.isRequired,
  title: PropTypes.string,
  variable: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    initial_value: PropTypes.number,
    type: PropTypes.string,
    unit: PropTypes.string,
    scope: PropTypes.string
  }).isRequired,
  errors: PropTypes.shape({
    name: PropTypes.any,
    description: PropTypes.any,
    initial_value: PropTypes.any,
    type: PropTypes.any,
    unit: PropTypes.any
  })
}

AttributeVariableForm.defaultProps = {
  setOpenDialogVariable: () => {},
  dispatchAction: () => {},
  title: null,
  variable: defaultValue,
  errors: null
}
